<template>
  <div class="box" :id="offerId" :class="{ 'is-highlighted': selected }">
    <p
      class="box has-text-weight-bold warning-box message has-background-warning"
      v-for="(err, idx) in errors"
      :key="idx"
    >
      <i class="fa fa-exclamation-triangle icon"></i>
      <span>{{
        $t([
          err.text,
          Number(err.screenId) + 1,
          getFormattedOfferNames(err.overlappingOffers)
        ])
      }}</span>
    </p>
    <div class="field is-grouped is-grouped-multiline">
      <input-field
        class="control offer-conf-field"
        v-model="offerName"
        :label="$t('Offer name')"
        :errors="offerName_errorsIfTouched"
        @focus="offerName_onFocus"
        maxlength="21"
      />
      <button-field
        class="control offer-conf-field template-button"
        :value="$t(offerTemplateId)"
        :label="$t('Template')"
        icon="fa-exchange"
        :errors="offerTemplateId_errors"
        @click="changingTemplate = true"
      />
      <multiselect-field
        class="control offer-conf-field"
        v-model="schedulingDays"
        :label="$t('Active in days')"
        icon="fa fa-calendar"
        :disabled="schedulingAlwayActive"
      />
      <multiselect-field
        class="control offer-conf-field"
        v-model="schedulingHours"
        :label="$t('Active in hours')"
        icon="fa fa-clock-o"
        :disabled="schedulingAlwayActive"
      />
      <toggle-button-field
        class="control offer-conf-field"
        v-model="schedulingAlwayActive"
        :label="$t('Always presented?')"
        :positiveLabel="$t('Yes')"
        :negativeLabel="$t('No')"
      />
    </div>

    <div class="field">
      <label class="label">{{ $t("Selected screens") }}</label>
      <div class="field is-grouped is-grouped-multiline screens">
        <div
          v-for="(_, screenId) in screens"
          :key="screenId"
          class="screen-preview-wrapper"
          :class="{ disabled: !offerScreens[screenId] }"
        >
          <div
            class="screen-preview"
            :class="{
              'has-background-warning': errorScreens.includes(Number(screenId))
            }"
          >
            <div class="screen-preview-header">
              <span class="icon"><i class="fa fa-tv"/></span>
              <span>{{ $t("Screen {0}", Number(screenId) + 1) }}</span>
              <switch-field
                class="control is-pulled-right"
                :value="offerScreens[screenId]"
                @change="toggleOfferScreen(screenId)"
              />
            </div>
            <screen-preview
              class="screen-preview-content"
              :screenId="screenId"
              :offerId="offerId"
              :templateId="offerTemplateId"
              :screens="offerScreens"
              @click.native="showScreenPreview(screenId)"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="field is-grouped is-grouped-multiline">
      <div class="control">
        <button
          class="button is-outlined is-primary"
          @click="saveOffer"
          :disabled="!group_valid || !group_dirty"
        >
          {{ $t("Save") }}
        </button>
        <field-message :success="$t(successMsg)" />
      </div>
      <button
        class="button control"
        @click="group_reset"
        :disabled="!group_dirty"
      >
        {{ $t("Reset changes") }}
      </button>
      <router-link
        class="button is-outlined"
        :to="`/projects/${projectId}/offers/${offerId}`"
      >
        <span>{{ $t("Go to Offer") }}</span>
      </router-link>
      <button
        class="button is-danger is-outlined control offer-remove-button"
        v-if="!isDraft"
        @click="deletingOffer = true"
      >
        {{ $t("Delete") }}
      </button>
      <button
        class="button is-danger is-outlined control offer-remove-button"
        v-if="isDraft"
        @click="deleteOffer"
      >
        {{ $t("Cancel") }}
      </button>
    </div>

    <modal-confirmation
      :is-open.sync="deletingOffer"
      :title="$t('Are you sure you want to delete this offer?')"
      :confirmText="$t('Delete')"
      @confirm="deleteOffer"
    >
      {{
        $t(
          "Once offer {0} is deleted, you will not be able to recover it!",
          offerName
        )
      }}
    </modal-confirmation>

    <modal-template-selector
      :is-open.sync="changingTemplate"
      :title="$t('Change template')"
      :templateId="offerTemplateId"
      :meals="sampleMeals"
      :confirmText="$t('Select')"
      @confirm="setOfferTemplate"
    />
    <screen-preview-overlay
      :isOpen="showingScreenPreviewId >= 0"
      :screenId="showingScreenPreviewId"
      :offerId="offerId"
      :templateId="offerTemplateId"
      :screens="offerScreens"
      @close="showScreenPreview(undefined)"
    >
    </screen-preview-overlay>
  </div>
</template>

<script>
import { state, actions } from "@/store"
import { validator, maxSize, required, nonBlank } from "@/validator"
import {
  InputField,
  MultiselectField,
  ToggleButtonField,
  SwitchField,
  FieldMessage,
  ButtonField
} from "@/components/form"
import { ScreenPreview, ScreenPreviewOverlay } from "@/components/screen"
import { ModalConfirmation, ModalTemplateSelector } from "@/components/modal"
import {
  offerSchedulerConvertor as convertor,
  resetTimeout,
  objectKeysEquals
} from "@/utils"
import { SAMPLE_MEAL } from "@/service/meal-constants"

const MSG_TIMEOUT_MS = 3000

export default {
  name: "offer-row",
  mixins: [validator],
  components: {
    InputField,
    ModalConfirmation,
    MultiselectField,
    ModalTemplateSelector,
    ToggleButtonField,
    SwitchField,
    FieldMessage,
    ButtonField,
    ScreenPreview,
    ScreenPreviewOverlay
  },
  props: ["offerId", "selected", "errors", "isDraft"],
  mounted() {
    if (this.selected) {
      this.scrollToThisOfferRow()
    }
  },
  data() {
    return {
      offerName: undefined,
      offerTemplateId: undefined,
      offerScreens: {},
      schedulingDays: [],
      schedulingHours: [],
      schedulingAlwayActive: undefined,
      deletingOffer: false,
      changingTemplate: false,
      successMsg: undefined,
      showingScreenPreviewId: undefined
    }
  },
  validations: {
    offerName: [maxSize(20), nonBlank, required],
    offerTemplateId: [required],
    schedulingDays: [required],
    schedulingHours: [required],
    schedulingAlwayActive: [required],
    offerScreens: [required]
  },
  watch: {
    "offer.conf.name": {
      immediate: true,
      handler(name) {
        this.offerName_reset(name)
      }
    },
    "offer.conf.templateId": {
      immediate: true,
      handler(templateId) {
        this.offerTemplateId_reset(templateId)
      }
    },
    "offer.conf.screens": {
      immediate: true,
      handler(screens, oldScreens) {
        if (!objectKeysEquals(screens, oldScreens)) {
          this.offerScreens_reset(screens)
        }
      }
    },
    "scheduling.days": {
      immediate: true,
      handler(days, oldDays) {
        if (!objectKeysEquals(days, oldDays)) {
          this.schedulingDays_reset(
            convertor.toDayValues(days, this.schedulerConf, this.$t)
          )
        }
      }
    },
    "scheduling.hours": {
      immediate: true,
      handler(hours, oldHours) {
        if (!objectKeysEquals(hours, oldHours)) {
          this.schedulingHours_reset(
            convertor.toHourValues(hours, this.schedulerConf, this.$t)
          )
        }
      }
    },
    "scheduling.alwaysActive": {
      immediate: true,
      handler(alwaysActive) {
        this.schedulingAlwayActive_reset(!!alwaysActive)
      }
    },
    schedulerConf: {
      immediate: true,
      handler(schedulerConf) {
        this.schedulingHours_reset(
          convertor.toHourValues(this.scheduling.hours, schedulerConf, this.$t)
        )
        this.schedulingDays_reset(
          convertor.toDayValues(this.scheduling.days, schedulerConf, this.$t)
        )
      }
    }
  },
  computed: {
    projectId() {
      return state.project.id
    },
    hasError() {
      return this.errors && this.errors.length > 0
    },
    errorScreens() {
      return this.errors ? this.errors.map(e => e.screenId) : []
    },
    schedulerConf() {
      return state.project.data.schedulingConf
    },
    offer() {
      const emptyOffer = {
        conf: {},
        data: {
          mealIds: {}
        }
      }
      return state.project.data.offers[this.offerId] || emptyOffer
    },
    templates() {
      return state.templates
    },
    sampleMeals() {
      const offerMeals = actions.getSortedOfferMeals(this.offerId)
      if (offerMeals.length > 0) {
        return offerMeals
      }
      const allMealIds = Object.keys(state.project.data.meals)
      if (allMealIds.length > 0) {
        return Object.values(state.project.data.meals)
      }
      return [SAMPLE_MEAL]
    },
    scheduling() {
      const emptyScheduling = {
        days: {},
        hours: {},
        alwaysActive: false
      }
      return state.project.data.scheduling[this.offerId] || emptyScheduling
    },
    screens() {
      const screens = {}
      for (let i = 0; i < state.project.data.screens; i++) {
        screens[i] = true
      }
      return screens
    }
  },
  methods: {
    showScreenPreview(screenId) {
      this.showingScreenPreviewId = screenId
    },
    saveOffer() {
      const conf = {
        name: this.offerName,
        templateId: this.offerTemplateId,
        screens: this.offerScreens
      }
      const scheduling = {
        days: convertor.toScheduling(this.schedulingDays),
        hours: convertor.toScheduling(this.schedulingHours),
        alwaysActive: this.schedulingAlwayActive
      }
      actions
        .saveConfAndSchedulingToOffer(this.offerId, conf, scheduling)
        .then(() => {
          this.successMsg = "Offer saved"
          resetTimeout(
            "offerChange" + this.offerId,
            () => {
              this.successMsg = undefined
            },
            MSG_TIMEOUT_MS
          )
        })
        .catch(actions.addError("modify-offer-error"))
    },
    deleteOffer() {
      actions
        .deleteOffer(this.offerId)
        .then(() => this.$emit("removed"))
        .catch(e => {
          this.deletingOffer = false
          actions.addError("remove-offer-error")(e)
        })
    },
    setOfferTemplate(templateId) {
      this.offerTemplateId = templateId
    },
    toggleOfferScreen(screenId) {
      const offerScreens = { ...this.offerScreens }
      if (offerScreens[screenId]) {
        delete offerScreens[screenId]
      } else {
        offerScreens[screenId] = true
      }
      this.offerScreens = offerScreens
    },
    scrollToThisOfferRow() {
      document.getElementById(this.offerId).scrollIntoView({})
    },
    getFormattedOfferNames(offerIds) {
      if (!offerIds || offerIds.length === 0) {
        return
      }
      const textOfferNames = offerIds.reduce((text, offerId, i) => {
        const separator = i === 0 ? "" : ", "
        return (text +=
          separator + state.project.data.offers[offerId].conf.name)
      }, "")
      return `(${textOfferNames})`
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/style.scss";

@keyframes in {
  from {
    border: 0.1rem solid rgba(0, 0, 0, 1);
  }
  to {
    border: 0.1rem solid rgba(255, 0, 0, 0);
  }
}

.box {
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  &.is-highlighted {
    animation: in 3s ease forwards 1;
  }
  .warning-box {
    padding-top: 0.75rem;
  }
  .offer-conf-field {
    flex: 1;
    min-width: 10rem;
  }
  .template-button {
    min-width: 15rem;
  }
  .offer-remove-button {
    margin-left: auto;
  }
  .screens {
    margin-right: -0.75rem;
    .screen-preview-wrapper {
      padding-bottom: 0.75rem;
      padding-right: 0.75rem;
      @include widescreen {
        flex: 0 0 25%;
      }
      @include desktop-only {
        flex: 0 0 33.333%;
      }
      @include tablet-only {
        flex: 0 0 50%;
      }
      @include mobile {
        flex: 0 0 100%;
      }
      .screen-preview {
        border: 1px solid $item-border-color;
        border-radius: 4px;
        padding: 0.25em;
        .screen-preview-header {
          position: relative;
          padding-bottom: 0.5rem;
        }
        .screen-preview-content {
          position: relative;
          cursor: pointer;
        }
      }
    }
    .screen-preview-wrapper.disabled {
      .screen-preview-content::before {
        content: "";
        position: absolute;
        height: 100%;
        width: 100%;
        background: black;
        opacity: 0.5;
        z-index: 10;
      }
    }
  }
}
</style>
